/**
 * View model for the Capacity & Utilisation screen (task 14.2, R6, R7.1).
 *
 * Turns the period list the calculation endpoint returns into KPI values, the
 * utilization grid of UI spec §5.1 and the reconciliation table of R6.5. The
 * four Bottleneck_State values (normal, warning, over_capacity,
 * data_incomplete) come from the server (R6.4) rather than being re-derived
 * here: the threshold comparison is the calculation service's, and duplicating
 * it in the client would be a second source of truth for the same rule.
 */
import {
  comparePeriodLabels,
  periodLabelOfRange,
  type PeriodLabel,
} from '../shared/period-label';
import type {
  CapacityUtilisationResponse,
  PeriodInitiative,
  UtilisationPeriod,
} from './capacity-utilisation-data';

/** A Bottleneck_State as R6.4 classifies it. */
export type BottleneckState = UtilisationPeriod['bottleneckState'];

/**
 * How a period should be named on screen.
 *
 * A period here is a Capacity_Period, not a calendar bucket:
 * `capacity-calculation-service.ts` states that "the Capacity_Period records
 * define the period grid (R6.1)", and the wire's `periodId` is that record's
 * primary key. A UUID is not a period name, so the label comes from the record's
 * effective range instead - and because most ranges line up with a month or a
 * full year, the common cases get a short name rather than a date range.
 *
 * A descriptor rather than a finished string: R18.9 keeps user-facing text in the
 * catalogue, and month names already live there under `month.*`. The page resolves
 * this through `t`; the view model stays translator-free.
 */
export type { DayParts, PeriodLabel } from '../shared/period-label';

/**
 * KPI summary values for the Capacity & Utilisation screen (R6.5, R7.1).
 *
 * Totals across every period in scope. `averageUtilisation` is `null` when no
 * period has a utilization figure at all - an average over nothing is not zero.
 * Data_Incomplete periods are excluded from the average (they have no
 * utilization to contribute) but counted in {@link dataIncompleteCount}.
 */
export interface CapacityUtilisationKpis {
  readonly totalDemandHours: number;
  readonly totalCapacityHours: number;
  readonly averageUtilisation: number | null;
  /**
   * Total Demand_Hours over total Capacity_Hours, as a percentage — the scope's
   * utilization as a single figure (R6.2).
   *
   * The headline figure, in preference to {@link averageUtilisation}: an unweighted
   * mean of per-period ratios counts a one-day period as heavily as a full quarter,
   * so a scope with one tiny idle period reads as far less loaded than it is. This
   * is the ratio of the two totals the cards beside it already show, so a reader can
   * check it against them. `null` when there is no capacity to divide by.
   */
  readonly overallUtilisation: number | null;
  readonly periodsInBreach: number;
  readonly dataIncompleteCount: number;
  /**
   * Periods classified Over_Capacity (R6.4) — the strict subset of
   * {@link periodsInBreach} whose demand actually exceeds capacity rather than
   * merely approaching the threshold.
   *
   * Reported separately because the two call for different responses: a period at
   * 92% of capacity is a watch item, while a period past 100% is a plan that
   * cannot be delivered as it stands. Counting them together, as
   * {@link periodsInBreach} alone did, left a reader unable to tell which they
   * were looking at.
   */
  readonly overCapacityCount: number;
  /**
   * Total hours of demand that exceed capacity, summed across the Over_Capacity
   * periods only (R6.5).
   *
   * Deliberately not the net of over- and under-used periods: spare hours in March
   * do not staff an overloaded June, so netting them would report a shortfall of
   * zero for a plan that cannot be delivered in either month. `0` therefore means
   * "no period is over capacity", and any positive figure is the work that has no
   * capacity behind it.
   */
  readonly totalExcessHours: number;
  /** Distinct Initiatives loading the scope, for the demand card's supporting line. */
  readonly contributingInitiativeCount: number;
  /** The Capacity_Threshold the server classified against (R6.4, R6.6). */
  readonly capacityThreshold: number;
  /** Demand_Hours by FF role with each role's share, descending (R7.1). */
  readonly roleSplit: readonly RoleDemandShare[];
  /**
   * The productive-hours and availability assumptions behind the capacity total,
   * or `null` when the scopes in view do not agree on them.
   *
   * `null` rather than a first-wins pick: with several Regions or roles in view the
   * assumptions can legitimately differ, and printing one scope's figures as though
   * they explained the whole total would be a plausible-looking lie. The page says
   * "mixed" instead.
   */
  readonly capacityAssumptions: CapacityAssumptions | null;
  /** How many distinct Regions the total covers, for the utilisation card's line. */
  readonly regionCount: number;
  /** The FF roles the total covers, in the order the server reported them. */
  readonly ffRoles: readonly string[];
}

/** One FF role's share of Demand_Hours (R7.1). */
export interface RoleDemandShare {
  readonly ffRole: string;
  readonly demandHours: number;
  /** Percentage of the scope's total Demand_Hours; `0` when there is no demand at all. */
  readonly sharePercent: number;
}

/** The two rate assumptions of R3.5's capacity formula, when every scope in view agrees. */
export interface CapacityAssumptions {
  readonly productiveHoursPerWorkday: number;
  readonly availabilityFactor: number;
}

/** One cell in the utilization grid (R6.2, R6.4, UI spec §5.1). */
export interface UtilisationGridCell {
  /** The period's range key. A React key and a drill-through handle, never a label. */
  readonly periodId: string;
  readonly periodLabel: PeriodLabel;
  readonly demandHours: number;
  readonly capacityHours: number | null;
  readonly utilisation: number | null;
  readonly bottleneckState: BottleneckState;
  /**
   * How much of the cell's track the bar fills, 0-100.
   *
   * Clamped at 100 because the track is the capacity, so a bar cannot overrun it —
   * past that point the *colour* carries the overload and the printed percentage
   * gives the real figure. Left at 0 for Data_Incomplete, where drawing any bar
   * would imply a measured load.
   */
  readonly barPercent: number;
  /** The Initiatives loading this period, descending, for the drill-through (R6.5). */
  readonly contributingInitiatives: readonly PeriodInitiative[];
}

/**
 * One row in the reconciliation table (R6.5, UI spec §5.1).
 *
 * `excessOrRemainingHours` is positive when demand exceeds capacity and negative
 * when capacity is left over, so one signed number covers both directions;
 * `excessOrRemainingLabel` renders it with the direction spelled out.
 */
export interface ReconciliationRow {
  /** The Capacity_Period's id. A React key and a drill-through handle, never a label. */
  readonly periodId: string;
  readonly periodLabel: PeriodLabel;
  readonly effectivePeriodStart: string;
  readonly effectivePeriodEnd: string;
  readonly demandHours: number;
  readonly capacityHours: number | null;
  readonly utilisation: number | null;
  readonly excessOrRemainingHours: number | null;
  /**
   * Which way the period sits against its capacity, so the page can pick the
   * wording without re-deriving the sign (R18.9 keeps the wording itself in the
   * catalogue).
   */
  readonly excessDirection: 'over' | 'free' | 'exact' | 'unknown';
  readonly bottleneckState: BottleneckState;
  /**
   * The single largest contributor to this period's Demand_Hours, or `null` when
   * nothing loads it.
   *
   * The top one rather than a count: "what is driving this period" is answered by a
   * name, and the full list is one click away in the drill-through.
   */
  readonly topInitiative: PeriodInitiative | null;
  /** Every Initiative loading this period, descending by hours (R6.5). */
  readonly contributingInitiatives: readonly PeriodInitiative[];
  /** Contributing scopes with no agreed headcount, excluded from the capacity figure (R3.4). */
  readonly dataIncompleteScopeCount: number;
}

/** One cell of the utilisation matrix: one FF role in one period (R6.2-R6.4, R7.1). */
export interface UtilisationMatrixCell {
  readonly periodId: string;
  readonly periodLabel: PeriodLabel;
  readonly demandHours: number;
  readonly capacityHours: number | null;
  readonly utilisation: number | null;
  readonly bottleneckState: BottleneckState;
  /** How much of the cell's track the bar fills, 0-100 — see {@link UtilisationGridCell.barPercent}. */
  readonly barPercent: number;
  readonly contributingInitiatives: readonly PeriodInitiative[];
  /**
   * Whether this role has a Capacity_Period covering the column at all.
   *
   * `false` renders as an empty cell rather than as `0%`: a role with no pool in a
   * period has no utilization, which is a different statement from a pool that is
   * idle, and printing zero would assert the second.
   */
  readonly present: boolean;
}

/** One row of the utilisation matrix: one FF role across every period. */
export interface UtilisationMatrixRow {
  readonly ffRole: string;
  readonly cells: readonly UtilisationMatrixCell[];
  /** The role's utilization across the whole scope, for the row's accessible summary. */
  readonly overallUtilisation: number | null;
}

/** The utilisation matrix: FF role rows against period columns (R7.1). */
export interface UtilisationMatrix {
  readonly periodLabels: readonly { readonly periodId: string; readonly label: PeriodLabel }[];
  readonly rows: readonly UtilisationMatrixRow[];
}

/**
 * Builds the role-by-period utilisation matrix (R6.2-R6.4, R7.1).
 *
 * Every classification and every percentage comes from the server's own per-role
 * slice; this only pivots them. Re-deriving a role's utilisation here from its
 * capacity and demand would put the threshold comparison in a second place, which is
 * exactly what R6.6 depends on there not being.
 *
 * Columns are the periods in chronological order, and every row carries a cell for
 * every column — including the roles with no pool in a given period, marked absent —
 * because a matrix with ragged rows cannot be read down a column.
 */
export function deriveUtilisationMatrix(
  periods: readonly UtilisationPeriod[],
): UtilisationMatrix {
  const ordered = [...periods]
    .map((period) => ({
      period,
      label: periodLabelOfRange(period.effectivePeriodStart, period.effectivePeriodEnd),
    }))
    .sort((a, b) => comparePeriodLabels(a.label, b.label));

  const roles = [
    ...new Set(ordered.flatMap(({ period }) => period.byRole.map((role) => role.ffRole))),
  ].sort();

  return {
    periodLabels: ordered.map(({ period, label }) => ({ periodId: period.periodId, label })),
    rows: roles.map((ffRole): UtilisationMatrixRow => {
      let demandTotal = 0;
      let capacityTotal = 0;

      const cells = ordered.map(({ period, label }): UtilisationMatrixCell => {
        const slice = period.byRole.find((candidate) => candidate.ffRole === ffRole);
        if (slice === undefined) {
          return {
            periodId: period.periodId,
            periodLabel: label,
            demandHours: 0,
            capacityHours: null,
            utilisation: null,
            bottleneckState: 'data_incomplete',
            barPercent: 0,
            contributingInitiatives: [],
            present: false,
          };
        }

        demandTotal += slice.demandHours;
        if (slice.capacityHours !== null) {
          capacityTotal += slice.capacityHours;
        }

        return {
          periodId: period.periodId,
          periodLabel: label,
          demandHours: slice.demandHours,
          capacityHours: slice.capacityHours,
          utilisation: slice.utilisation,
          bottleneckState: slice.bottleneckState,
          barPercent: slice.utilisation === null ? 0 : Math.min(slice.utilisation, 100),
          contributingInitiatives: slice.contributingInitiatives,
          present: true,
        };
      });

      return {
        ffRole,
        cells,
        overallUtilisation: capacityTotal > 0 ? (demandTotal / capacityTotal) * 100 : null,
      };
    }),
  };
}

/**
 * Derives the KPI summary from the calculation response (R6.5, R7.1).
 *
 * Takes the whole response rather than just its periods: the supporting line under
 * each headline figure is drawn from scope-level facts the period list does not
 * carry — the initiative count, the threshold classified against, the role split
 * and how many Regions the total covers.
 */
export function deriveCapacityUtilisationKpis(
  response: CapacityUtilisationResponse,
): CapacityUtilisationKpis {
  const periods = response.periods;
  let totalDemandHours = 0;
  let totalCapacityHours = 0;
  let periodsInBreach = 0;
  let dataIncompleteCount = 0;
  let overCapacityCount = 0;
  let totalExcessHours = 0;
  let utilisationSum = 0;
  let utilisationCount = 0;

  for (const period of periods) {
    totalDemandHours += period.demandHours;

    if (period.capacityHours !== null) {
      totalCapacityHours += period.capacityHours;
    }

    if (period.utilisation !== null) {
      utilisationSum += period.utilisation;
      utilisationCount += 1;
    }

    if (period.bottleneckState === 'warning' || period.bottleneckState === 'over_capacity') {
      periodsInBreach += 1;
    }

    if (period.bottleneckState === 'over_capacity') {
      overCapacityCount += 1;
      // Guarded on the state, not on the sign of the difference: an Over_Capacity
      // period always has a capacity figure (R6.3 sends anything else to
      // data_incomplete), and the null check is what proves that to the compiler
      // rather than a non-null assertion standing in for the requirement.
      if (period.capacityHours !== null) {
        totalExcessHours += period.demandHours - period.capacityHours;
      }
    }

    if (period.bottleneckState === 'data_incomplete') {
      dataIncompleteCount += 1;
    }
  }

  return {
    totalDemandHours,
    totalCapacityHours,
    averageUtilisation: utilisationCount > 0 ? utilisationSum / utilisationCount : null,
    overallUtilisation:
      totalCapacityHours > 0 ? (totalDemandHours / totalCapacityHours) * 100 : null,
    periodsInBreach,
    dataIncompleteCount,
    overCapacityCount,
    totalExcessHours,
    contributingInitiativeCount: response.contributingInitiativeCount,
    capacityThreshold: response.capacityThreshold,
    roleSplit: deriveRoleSplit(response.demandByRole),
    capacityAssumptions: deriveCapacityAssumptions(periods),
    regionCount: response.regionCount,
    ffRoles: response.ffRoles,
  };
}

/**
 * The per-role demand split, descending by hours (R7.1).
 *
 * Shares are of the *summed* role totals rather than of the periods' demand: the
 * two agree, but summing the same map the percentages are taken from means a split
 * always adds to 100% rather than to something near it. A scope with no demand
 * yields no rows, not rows of `0%`.
 */
function deriveRoleSplit(
  demandByRole: Readonly<Record<string, number>>,
): readonly RoleDemandShare[] {
  const entries = Object.entries(demandByRole);
  const total = entries.reduce((sum, [, hours]) => sum + hours, 0);

  return entries
    .map(([ffRole, demandHours]) => ({
      ffRole,
      demandHours,
      sharePercent: total === 0 ? 0 : (demandHours / total) * 100,
    }))
    .sort((a, b) => b.demandHours - a.demandHours || a.ffRole.localeCompare(b.ffRole));
}

/**
 * The productive-hours and availability assumptions, when every Capacity_Period in
 * view agrees on both; `null` when they differ.
 *
 * See {@link CapacityUtilisationKpis.capacityAssumptions} for why disagreement is
 * reported as absence rather than resolved by picking one.
 */
function deriveCapacityAssumptions(
  periods: readonly UtilisationPeriod[],
): CapacityAssumptions | null {
  const distinct = new Set<string>();
  let first: CapacityAssumptions | null = null;

  for (const period of periods) {
    for (const input of period.capacityInputs) {
      distinct.add(`${String(input.productiveHoursPerWorkday)}|${String(input.availabilityFactor)}`);
      first ??= {
        productiveHoursPerWorkday: input.productiveHoursPerWorkday,
        availabilityFactor: input.availabilityFactor,
      };
    }
  }

  return distinct.size === 1 ? first : null;
}

/**
 * Transforms the period list into grid cells (R6.2, R6.4, UI spec §5.1).
 *
 * Ordered by the effective period's start date. Sorting by `periodId` would be
 * sorting by UUID, which is arbitrary - the ids here are Capacity_Period primary
 * keys, not the sortable `2027-01` identifiers a calendar grid would carry.
 */
export function deriveUtilisationGridCells(
  periods: readonly UtilisationPeriod[],
): readonly UtilisationGridCell[] {
  return periods
    .map((period) => ({
      periodId: period.periodId,
      periodLabel: periodLabelOfRange(period.effectivePeriodStart, period.effectivePeriodEnd),
      demandHours: period.demandHours,
      capacityHours: period.capacityHours,
      utilisation: period.utilisation,
      bottleneckState: period.bottleneckState,
      barPercent: period.utilisation === null ? 0 : Math.min(period.utilisation, 100),
      contributingInitiatives: period.contributingInitiatives,
    }))
    .sort((a, b) => comparePeriodLabels(a.periodLabel, b.periodLabel));
}

/** Transforms the period list into reconciliation rows (R6.5, UI spec §5.1). */
export function deriveReconciliationRows(
  periods: readonly UtilisationPeriod[],
): readonly ReconciliationRow[] {
  return periods
    .map((period): ReconciliationRow => {
      const excessOrRemainingHours =
        period.capacityHours === null ? null : period.demandHours - period.capacityHours;

      return {
        periodId: period.periodId,
        periodLabel: periodLabelOfRange(period.effectivePeriodStart, period.effectivePeriodEnd),
        effectivePeriodStart: period.effectivePeriodStart,
        effectivePeriodEnd: period.effectivePeriodEnd,
        demandHours: period.demandHours,
        capacityHours: period.capacityHours,
        utilisation: period.utilisation,
        excessOrRemainingHours,
        excessDirection: excessDirectionOf(excessOrRemainingHours),
        bottleneckState: period.bottleneckState,
        // The list arrives already sorted descending from the server, so the first
        // entry is the top contributor; `?? null` covers a period nothing loads.
        topInitiative: period.contributingInitiatives[0] ?? null,
        contributingInitiatives: period.contributingInitiatives,
        dataIncompleteScopeCount: period.dataIncompleteScopeCount,
      };
    })
    .sort((a, b) => comparePeriodLabels(a.periodLabel, b.periodLabel));
}

/** Which side of its capacity a period falls on (R6.5). */
function excessDirectionOf(hours: number | null): ReconciliationRow['excessDirection'] {
  if (hours === null) {
    return 'unknown';
  }
  if (hours > 0) {
    return 'over';
  }
  if (hours < 0) {
    return 'free';
  }
  return 'exact';
}

/**
 * The Bottleneck_State badge itself is `components/ui/capacity-state-badge.tsx`.
 *
 * This model used to return the badge's label key and Tailwind classes for the
 * page to assemble, which put a presentation decision in a view model and drifted:
 * the classes were outline-only pills where UI spec §3.4 specifies a tinted 3px
 * pill, and the Data_Incomplete case set `text-muted` — the shadcn *surface* token
 * rather than the muted *text* token — so that label rendered near-white on a
 * near-white fill. The pill is now a component beside `StatusBadge`, which is what
 * keeps the two consistent in the tables where both appear.
 */

/**
 * The saturated bar colour for a Bottleneck_State, for the utilisation strip's
 * proportional bars (R6.4, UI spec §5.1).
 *
 * The full-strength semantic tokens rather than the tints
 * {@link getUtilisationCellFillColor} returns: a bar is a shape read against an
 * empty track, so it needs to be solid, where a whole-cell fill has to stay light
 * enough for text to sit on top of it. The percentage is printed outside the bar
 * for exactly that reason, which is also what keeps the state legible when colour
 * is not perceived (R10.6, R18.8).
 */
export function getUtilisationBarColor(state: BottleneckState): string {
  switch (state) {
    case 'normal':
      return 'var(--ok)';
    case 'warning':
      return 'var(--warn)';
    case 'over_capacity':
      return 'var(--risk)';
    case 'data_incomplete':
      return 'var(--grey)';
  }
}

/** The grid cell fill for a Bottleneck_State (R6.4, UI spec §5.1). */
export function getUtilisationCellFillColor(state: BottleneckState): string {
  switch (state) {
    case 'normal':
      return 'color-mix(in srgb, var(--ok) 18%, transparent)';
    case 'warning':
      return 'color-mix(in srgb, var(--warn) 22%, transparent)';
    case 'over_capacity':
      return 'color-mix(in srgb, var(--risk) 24%, transparent)';
    case 'data_incomplete':
      return 'var(--line-soft)';
  }
}
